import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  user: null,
  originalUser: null, // For rollback functionality
  isEditing: false,
  isLoading: false,
  error: null,
  uploadProgress: 0,
  hasUnsavedChanges: false,
  lastSavedAt: null,
  validationErrors: {},
  isDirty: false,
  optimisticUpdates: [], // Track optimistic updates for rollback
};

const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    // Edit mode management
    setEditing: (state, action) => {
      state.isEditing = action.payload;
      if (action.payload) {
        state.originalUser = { ...state.user }; // Store original for rollback
        state.error = null;
        state.validationErrors = {};
      }
    },
    startEditing: (state) => {
      state.isEditing = true;
      state.originalUser = { ...state.user }; // Store original for rollback
      state.error = null;
      state.validationErrors = {};
    },
    cancelEditing: (state) => {
      state.isEditing = false;
      state.user = state.originalUser; // Rollback to original
      state.originalUser = null;
      state.hasUnsavedChanges = false;
      state.isDirty = false;
      state.validationErrors = {};
      state.error = null;
    },
    stopEditing: (state) => {
      state.isEditing = false;
      state.originalUser = null;
      state.hasUnsavedChanges = false;
      state.isDirty = false;
      state.validationErrors = {};
    },

    // User data management
    setUser: (state, action) => {
      state.user = action.payload;
      state.hasUnsavedChanges = false;
      state.isDirty = false;
    },
    updateUser: (state, action) => {
      state.user = { ...state.user, ...action.payload };
      state.hasUnsavedChanges = true;
      state.isDirty = true;
    },
    updateUserField: (state, action) => {
      const { field, value } = action.payload;
      state.user[field] = value;
      state.hasUnsavedChanges = true;
      state.isDirty = true;
      
      // Clear field-specific validation error
      if (state.validationErrors[field]) {
        delete state.validationErrors[field];
      }
    },

    // Optimistic updates with rollback
    addOptimisticUpdate: (state, action) => {
      const { id, data, originalData } = action.payload;
      state.optimisticUpdates.push({ id, data, originalData });
      state.user = { ...state.user, ...data };
    },
    commitOptimisticUpdate: (state, action) => {
      const { id } = action.payload;
      state.optimisticUpdates = state.optimisticUpdates.filter(update => update.id !== id);
    },
    rollbackOptimisticUpdate: (state, action) => {
      const { id } = action.payload;
      const update = state.optimisticUpdates.find(update => update.id === id);
      if (update) {
        state.user = { ...state.user, ...update.originalData };
        state.optimisticUpdates = state.optimisticUpdates.filter(update => update.id !== id);
      }
    },
    rollbackAllOptimisticUpdates: (state) => {
      state.optimisticUpdates.forEach(update => {
        state.user = { ...state.user, ...update.originalData };
      });
      state.optimisticUpdates = [];
    },

    // Loading and error states
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    clearError: (state) => {
      state.error = null;
    },

    // Validation errors
    setValidationErrors: (state, action) => {
      state.validationErrors = action.payload;
    },
    addValidationError: (state, action) => {
      const { field, error } = action.payload;
      state.validationErrors[field] = error;
    },
    clearValidationErrors: (state) => {
      state.validationErrors = {};
    },
    clearFieldValidationError: (state, action) => {
      const field = action.payload;
      if (state.validationErrors[field]) {
        delete state.validationErrors[field];
      }
    },

    // Upload progress
    setUploadProgress: (state, action) => {
      state.uploadProgress = action.payload;
    },
    resetUploadProgress: (state) => {
      state.uploadProgress = 0;
    },

    // Save state tracking
    setUnsavedChanges: (state, action) => {
      state.hasUnsavedChanges = action.payload;
      state.isDirty = action.payload;
    },
    markSaved: (state) => {
      state.hasUnsavedChanges = false;
      state.isDirty = false;
      state.lastSavedAt = new Date().toISOString();
      state.originalUser = { ...state.user }; // Update original after successful save
    },
    markUnsaved: (state) => {
      state.hasUnsavedChanges = true;
      state.isDirty = true;
    },

    // Reset profile state
    resetProfile: (_state) => {
      return { ...initialState };
    },

    // Async action handlers
    fetchProfileStart: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    fetchProfileSuccess: (state, action) => {
      state.user = action.payload;
      state.originalUser = { ...action.payload };
      state.isLoading = false;
      state.error = null;
      state.hasUnsavedChanges = false;
      state.isDirty = false;
    },
    fetchProfileFailure: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    },

    updateProfileStart: (state) => {
      state.isLoading = true;
      state.error = null;
      state.validationErrors = {};
    },
    updateProfileSuccess: (state, action) => {
      const { userData, keepEditMode = false } = action.payload;
      state.user = { ...state.user, ...userData };
      state.originalUser = { ...state.user };
      state.isLoading = false;
      state.error = null;
      state.hasUnsavedChanges = false;
      state.isDirty = false;
      state.lastSavedAt = new Date().toISOString();
      state.validationErrors = {};
      
      // Only exit edit mode if keepEditMode is false
      if (!keepEditMode) {
        state.isEditing = false;
      }
    },
    updateProfileFailure: (state, action) => {
      state.isLoading = false;
      state.error = action.payload.message || action.payload;
      
      // Handle validation errors
      if (action.payload.validationErrors) {
        state.validationErrors = action.payload.validationErrors;
      }
      
      // Rollback optimistic updates on failure
      if (state.optimisticUpdates.length > 0) {
        state.optimisticUpdates.forEach(update => {
          state.user = { ...state.user, ...update.originalData };
        });
        state.optimisticUpdates = [];
      }
    },

    changePasswordStart: (state) => {
      state.isLoading = true;
      state.error = null;
      state.validationErrors = {};
    },
    changePasswordSuccess: (state) => {
      state.isLoading = false;
      state.error = null;
      state.validationErrors = {};
    },
    changePasswordFailure: (state, action) => {
      state.isLoading = false;
      state.error = action.payload.message || action.payload;
      
      if (action.payload.validationErrors) {
        state.validationErrors = action.payload.validationErrors;
      }
    },

    uploadProfilePictureStart: (state) => {
      state.isLoading = true;
      state.error = null;
      state.uploadProgress = 0;
    },
    uploadProfilePictureProgress: (state, action) => {
      state.uploadProgress = action.payload;
    },
    uploadProfilePictureSuccess: (state, action) => {
      state.user.profile_picture = action.payload.profile_picture;
      state.originalUser.profile_picture = action.payload.profile_picture;
      state.isLoading = false;
      state.error = null;
      state.uploadProgress = 100;
      state.lastSavedAt = new Date().toISOString();
    },
    uploadProfilePictureFailure: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
      state.uploadProgress = 0;
    },
  },
});

export const {
  // Edit mode
  setEditing,
  startEditing,
  cancelEditing,
  stopEditing,
  
  // User data
  setUser,
  updateUser,
  updateUserField,
  
  // Optimistic updates
  addOptimisticUpdate,
  commitOptimisticUpdate,
  rollbackOptimisticUpdate,
  rollbackAllOptimisticUpdates,
  
  // Loading and errors
  setLoading,
  setError,
  clearError,
  
  // Validation
  setValidationErrors,
  addValidationError,
  clearValidationErrors,
  clearFieldValidationError,
  
  // Upload progress
  setUploadProgress,
  resetUploadProgress,
  
  // Save state
  setUnsavedChanges,
  markSaved,
  markUnsaved,
  
  // Reset
  resetProfile,
  
  // Async actions
  fetchProfileStart,
  fetchProfileSuccess,
  fetchProfileFailure,
  updateProfileStart,
  updateProfileSuccess,
  updateProfileFailure,
  changePasswordStart,
  changePasswordSuccess,
  changePasswordFailure,
  uploadProfilePictureStart,
  uploadProfilePictureProgress,
  uploadProfilePictureSuccess,
  uploadProfilePictureFailure,
} = profileSlice.actions;

// Selectors
export const selectProfileState = (state) => state.profile;
export const selectUser = (state) => state.profile.user;
export const selectOriginalUser = (state) => state.profile.originalUser;
export const selectIsEditing = (state) => state.profile.isEditing;
export const selectIsLoading = (state) => state.profile.isLoading;
export const selectError = (state) => state.profile.error;
export const selectUploadProgress = (state) => state.profile.uploadProgress;
export const selectHasUnsavedChanges = (state) => state.profile.hasUnsavedChanges;
export const selectIsDirty = (state) => state.profile.isDirty;
export const selectLastSavedAt = (state) => state.profile.lastSavedAt;
export const selectValidationErrors = (state) => state.profile.validationErrors;
export const selectFieldValidationError = (field) => (state) => state.profile.validationErrors[field];
export const selectHasValidationErrors = (state) => Object.keys(state.profile.validationErrors).length > 0;
export const selectOptimisticUpdates = (state) => state.profile.optimisticUpdates;
export const selectHasOptimisticUpdates = (state) => state.profile.optimisticUpdates.length > 0;
export const selectCanSave = (state) => {
  const { hasUnsavedChanges, isLoading, validationErrors } = state.profile;
  return hasUnsavedChanges && !isLoading && Object.keys(validationErrors).length === 0;
};
export const selectUserField = (field) => (state) => state.profile.user?.[field];

export default profileSlice.reducer;